/**
 * Persisted keyboard-shortcut overrides (plan D2) with the D6 conflict model
 * applied on every change. Durable state is `{ scheme, overrides }`; keymap,
 * notices and unknown ids are derived. Every change is ONE store update of the
 * settled overrides, keymap and notices (`deriveKeybindings` → `settleKeymap`),
 * so a displaced binding is persisted by the same write and the committed
 * keymap is the one a reload computes. Re-validation runs on every published
 * keyboard-layout revision (the cache refreshes on window focus). Details:
 * Docs/Keybindings.md.
 */
import { create, type StoreApi } from 'zustand';
import { persist } from 'zustand/middleware';
import { createGuardedJsonStorage } from '../../../core/storage/guardedJsonStorage';
import { showPersistWriteFailureToast } from '../../../components/ui/persistWriteFailureToast';
import { normalizeChord } from '../model/chord';
import { platform } from '../model/platform';
import { resolveKeymap } from '../model/resolveKeymap';
import type { KeybindingCommandId } from '../model/keybindingTypes';
import { deriveKeybindings } from './deriveKeybindings';
import { dataOf, hydrateKeybindings } from './hydrateKeybindings';
import { availableKeybindingSchemes, schemeFor } from './keybindingSchemes';
import { keyboardLayoutMap } from './keyboardLayout';
import { isKnownCommandId, MAX_CHORDS_PER_COMMAND, normalizeCommandChords } from './normalizeOverrides';
import { addedChords, findSameChord, pickOverrides, withOverride, withoutChordsLostToOverrides } from './overrideEdits';
import { canReclaim } from './keybindingsSelectors';
import type {
  KeybindingsSnapshot,
  KeybindingsState,
  KeybindingsStoreEnvironment,
} from './keybindingsStoreTypes';

const EMPTY_SNAPSHOT: KeybindingsSnapshot = { scheme: 'jam', overrides: {} };

const hasOwn = (record: object, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(record, key);

export function createKeybindingsStore(environment: KeybindingsStoreEnvironment) {
  let writeBackAfterHydration = false;
  let storeApi: StoreApi<KeybindingsState> | null = null;

  const store = create<KeybindingsState>()(
    persist(
      (set, get, api) => {
        storeApi = api;

        /** The command's durable chords (override or scheme), before validation. */
        const durableChords = (commandId: KeybindingCommandId, overrides = get().overrides): readonly string[] =>
          resolveKeymap(schemeFor(get().scheme, environment.schemes), overrides, environment.platform)[commandId];
        type Mutation = { readonly commandId: KeybindingCommandId; readonly addedChords: readonly string[] };
        type Accept = (derived: ReturnType<typeof deriveKeybindings>) => boolean;
        /**
         * A user change (one write, undo snapshot; its durable notices are
         * undoable): its added chords win collisions and only they can be refused
         * as reserved. `accept` can veto the settled result — nothing is written.
         */
        const commit = (next: KeybindingsSnapshot, mutation?: Mutation, accept?: Accept): boolean => {
          const current = get();
          const derived = deriveKeybindings(next, environment, {
            mutatedCommandId: mutation?.commandId,
            mutatedChords: mutation?.addedChords,
            previous: current,
            origin: 'mutation',
          });
          if (accept && !accept(derived)) return false;
          set({
            ...dataOf(derived),
            undoSnapshot: { scheme: current.scheme, overrides: current.overrides },
          });
          return true;
        };
        const sameIn = (chords: readonly string[], chord: string) =>
          findSameChord(chords, chord, environment.platform, environment.layout.getSnapshot());
        /** `added` defaults to the chords the new list holds that the old one did not (by identity). */
        const setOverride = (
          commandId: KeybindingCommandId, chords: readonly string[] | undefined, added?: readonly string[], accept?: Accept,
        ): boolean => {
          const { scheme, overrides, notices } = get();
          const chordsAdded = added ?? addedChords(durableChords(commandId), durableChords(commandId,
            withOverride(overrides, commandId, chords)), environment.platform, environment.layout.getSnapshot());
          const owned = chords && withoutChordsLostToOverrides(chords, commandId, notices, overrides, chordsAdded);
          return commit({ scheme, overrides: withOverride(overrides, commandId, owned) }, { commandId, addedChords: chordsAdded }, accept);
        };

        return {
          ...dataOf(deriveKeybindings(EMPTY_SNAPSHOT, environment)),
          undoSnapshot: null,

          setBinding: (commandId, chords) => {
            const normalized = normalizeCommandChords(chords).chords;
            // Only an explicit empty list unbinds; unreadable input changes nothing.
            if (chords.length > 0 && normalized.length === 0) return;
            // Hydration keeps at most MAX_CHORDS_PER_COMMAND, so more is refused, not truncated later.
            const distinct = new Set(chords.map((chord) => normalizeChord(chord)).filter((chord) => chord !== null));
            if (distinct.size > MAX_CHORDS_PER_COMMAND) return;
            setOverride(commandId, normalized);
          },
          addBinding: (commandId, chord) => {
            const normalized = normalizeChord(chord);
            const current = durableChords(commandId);
            if (normalized === null || sameIn(current, normalized) !== undefined) return;
            if (current.length >= MAX_CHORDS_PER_COMMAND) return;
            setOverride(commandId, [...current, normalized]);
          },
          removeBinding: (commandId, chord) => {
            const normalized = normalizeChord(chord) ?? chord;
            const current = durableChords(commandId);
            if (!current.includes(normalized)) return;
            setOverride(commandId, current.filter((item) => item !== normalized));
          },
          claimBinding: (commandId, chord, dropChord) => {
            // Re-recording an inactive chord: the list (minus `dropChord`), with
            // that chord counted as added, so it wins its collision durably.
            const current = durableChords(commandId);
            const stored = sameIn(current, normalizeChord(chord) ?? chord);
            if (stored === undefined || !canReclaim(get(), commandId, stored)) return false;
            // Commit only if the claim actually leaves the chord active once
            // settled (J3) — otherwise it would displace others for nothing.
            return setOverride(commandId, current.filter((item) => item !== dropChord), [stored],
              (derived) => derived.keymap[commandId].includes(stored));
          },
          unbind: (commandId) => setOverride(commandId, []),
          resetCommand: (commandId) => {
            if (!hasOwn(get().overrides, commandId)) return;
            setOverride(commandId, undefined);
          },
          resetAll: () => {
            const { scheme, overrides } = get();
            // Ids from another app version are not shortcuts of this build;
            // they are removed only by `dropUnknownOverrides`.
            const kept = pickOverrides(overrides, (id) => !isKnownCommandId(id));
            if (Object.keys(kept).length === Object.keys(overrides).length) return;
            commit({ scheme, overrides: kept });
          },
          setScheme: (schemeId) => {
            const { scheme, overrides } = get();
            if (schemeId === scheme || !hasOwn(environment.schemes, schemeId)) return;
            commit({ scheme: schemeId, overrides });
          },
          dropUnknownOverrides: () => {
            const { scheme, overrides, unknownOverrideIds } = get();
            if (unknownOverrideIds.length === 0) return;
            commit({ scheme, overrides: pickOverrides(overrides, isKnownCommandId) });
          },
          undoLastNotice: () => {
            const { undoSnapshot } = get();
            if (!undoSnapshot) return;
            const derived = deriveKeybindings(undoSnapshot, environment, { previous: get() });
            set({ ...dataOf(derived), undoSnapshot: null });
          },
          revalidate: () => {
            const current = get();
            const derived = deriveKeybindings(current, environment, {
              previous: current,
              carryDurableNotices: true,
            });
            const unchanged = !derived.patched && derived.keymap === current.keymap &&
              derived.notices === current.notices;
            // An automatic repair (J2): the old snapshot no longer reverses the
            // current state, so its Undo goes away.
            if (!unchanged) set({ ...dataOf(derived), ...(derived.patched ? { undoSnapshot: null } : {}) });
          },
        };
      },
      {
        name: 'jam-sql-keybindings',
        // Crash-proof write (core/storage/guardedJsonStorage.ts). These are
        // settings the user chose, so a dropped write gets the user notice.
        storage: createGuardedJsonStorage<KeybindingsSnapshot>({
          onFailure: showPersistWriteFailureToast('your keyboard shortcuts', { plural: true }),
        }),
        // v1 is the first shape. `merge` normalizes anything that arrives —
        // older, newer or hand-edited — so the migration only hands it through.
        version: 1,
        migrate: (persistedState) => persistedState as KeybindingsSnapshot,
        partialize: (state): KeybindingsSnapshot => ({ scheme: state.scheme, overrides: state.overrides }),
        merge: (persistedState, currentState) => {
          const hydrated = hydrateKeybindings(persistedState, currentState, environment);
          writeBackAfterHydration = hydrated.writeBack;
          return hydrated.state;
        },
        onRehydrateStorage: () => () => {
          if (!writeBackAfterHydration) return;
          writeBackAfterHydration = false;
          // `persist` writes back only after a version migration; force one
          // write so a hydration patch is durable, not just in memory.
          storeApi?.setState({});
        },
      },
    ),
  );

  environment.layout.subscribe(() => store.getState().revalidate());
  return store;
}

export const useKeybindingsStore = createKeybindingsStore({
  platform: platform(),
  isDev: import.meta.env.DEV,
  layout: keyboardLayoutMap,
  schemes: availableKeybindingSchemes,
});
